import { z } from 'zod';

// Helper function to validate cloud Grafana URLs
const validateCloudUrl = (url: string): boolean => {
  let urlToCheck = url;
  if (urlToCheck.startsWith('https://')) {
    urlToCheck = urlToCheck.replace('https://', '');
  }

  if (!urlToCheck.endsWith('.grafana.net')) {
    return false;
  }
  const instanceName = urlToCheck.replace('.grafana.net', '');

  return (
    instanceName.length >= 1 &&
    instanceName.length <= 50 &&
    /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?$/.test(instanceName)
  );
};

// Helper function to validate self-hosted URLs
const validateSelfHostedUrl = (url: string): boolean => {
  let urlToValidate = url;
  if (
    !urlToValidate.startsWith('http://') &&
    !urlToValidate.startsWith('https://')
  ) {
    urlToValidate = `https://${urlToValidate}`;
  }

  try {
    new URL(urlToValidate);

    return true;
  } catch {
    return false;
  }
};

export const validationSchema = z
  .object({
    deploymentType: z.enum(['cloud', 'self-hosted'], {
      required_error:
        'i18n.integrations.monitoring.grafana.form.deploymentType.errors.required',
    }),
    instanceUrl: z.string().min(1, {
      message:
        'i18n.integrations.monitoring.grafana.form.instanceUrl.errors.required',
    }),
    serviceAccountDisplayName: z
      .string()
      .min(2, {
        message:
          'i18n.integrations.monitoring.grafana.form.serviceAccountDisplayName.errors.min|{"min": 2}',
      })
      .max(100, {
        message:
          'i18n.integrations.monitoring.grafana.form.serviceAccountDisplayName.errors.max|{"max": 100}',
      }),
    serviceAccountToken: z
      .string()
      .min(40, {
        message:
          'i18n.integrations.monitoring.grafana.form.serviceAccountToken.errors.min|{"min": 40}',
      })
      .max(500, {
        message:
          'i18n.integrations.monitoring.grafana.form.serviceAccountToken.errors.max|{"max": 500}',
      })
      .regex(/^glsa_/, {
        message:
          'i18n.integrations.monitoring.grafana.form.serviceAccountToken.errors.format',
      }),
  })
  .refine(
    data => {
      return data.deploymentType === 'cloud'
        ? validateCloudUrl(data.instanceUrl)
        : validateSelfHostedUrl(data.instanceUrl);
    },
    {
      message:
        'i18n.integrations.monitoring.grafana.form.instanceUrl.errors.invalid',
      path: ['instanceUrl'],
    },
  );

export type FormValues = z.infer<typeof validationSchema>;

// Convert frontend form values to backend payload
export type BackendPayload = {
  instanceUrl: string;
  serviceAccountDisplayName: string;
  serviceAccountToken: string;
  isSelfHosted: boolean;
};

export const defaultValues: FormValues = {
  deploymentType: 'cloud',
  instanceUrl: '',
  serviceAccountDisplayName: '',
  serviceAccountToken: '',
};

// Helper function to convert form values to backend payload
export const convertToBackendPayload = (
  values: FormValues,
): BackendPayload => ({
  instanceUrl: values.instanceUrl,
  serviceAccountDisplayName: values.serviceAccountDisplayName,
  serviceAccountToken: values.serviceAccountToken,
  isSelfHosted: values.deploymentType === 'self-hosted',
});
